import { put, select, takeEvery } from "redux-saga/effects";

import { SaleAction, setCurrentLivestockSaleId } from "actions";

import { patchDeploymentSale } from "actions/offline/deploymentSale";

import { SALE } from "constants/actionTypes";
import { SaleType } from "constants/sale";

import { integratedBillingDocumentDescription } from "lib/billingDocument";
import {
  getLivestockSaleId,
  getSaleEntryRoute,
  openConsignmentsTable,
  openEditConsignmentModal,
} from "lib/navigation";
import toast from "lib/toast";

import {
  getBillingRuns,
  getConsignments,
  getCurrentSale,
  getNominations,
  getSaleLots,
  selectBillingDocumentsWithActiveIntegrationByBillingRunId,
} from "selectors";

import history from "appHistory";

function onSaleCreateSuccess(action) {
  const { payload } = action;
  const { sale_type, saleyard_name, livestocksale_id } = payload;

  history.push(getSaleEntryRoute(saleyard_name, livestocksale_id, sale_type));
  if (sale_type === SaleType.PADDOCK) {
    // if we create a paddock sale, we want to direct the user
    // straight to the consignment form
    openConsignmentsTable();
    openEditConsignmentModal();
  }
}

function* onSaleFetchSuccess(action) {
  const { meta, payload } = action;
  const { options } = meta;
  const { livestocksale_id } = payload;
  const { openOnFetch = false } = options;
  if (openOnFetch) {
    yield put(setCurrentLivestockSaleId(livestocksale_id, true));
  }
}

function* onSaleUpdateFailure() {
  // refetch the sale to update state
  yield put(SaleAction.request());
}

export function* deleteCurrentSale() {
  const state = yield select();
  const sale = getCurrentSale(state);
  const saleLots = getSaleLots(state);
  const consignments = getConsignments(state);
  const nominations = getNominations(state);
  const billingRuns = getBillingRuns(state);
  const billingDocumentsWithActiveIntegrationByBillingRunId =
    selectBillingDocumentsWithActiveIntegrationByBillingRunId(state);

  // Check for billingDocuments with active integration status
  // Will be in state for Sundry sales and the like, but may not be for other sales.
  // Backend also prevents deletion when documents with active integrations exist.
  const billingRunsForSale = Object.values(billingRuns).filter(billingRun =>
    billingRun.livestockSaleIds.includes(sale.id),
  );
  const billingDocuments = billingRunsForSale.reduce((acc, billingRun) => {
    const billingDocumentsWithActiveIntegration =
      billingDocumentsWithActiveIntegrationByBillingRunId[billingRun.id] || [];
    acc.push(...billingDocumentsWithActiveIntegration);
    return acc;
  }, []);

  const errorMessage = name =>
    `You can’t delete a sale that contains any ${name}`;

  if (Object.values(saleLots).length > 0) {
    toast.error(errorMessage("lots"));
  } else if (Object.values(consignments).length > 0) {
    if (sale.sale_type?.includes(SaleType.CLEARING)) {
      toast.error(errorMessage("vendors"));
    } else {
      toast.error(errorMessage("consignments"));
    }
  } else if (Object.values(nominations).length > 0) {
    toast.error(errorMessage("nominations"));
  } else if (billingDocuments.length > 0) {
    const document_descriptions = billingDocuments.map(document =>
      integratedBillingDocumentDescription(document),
    );
    const errorMessageText = `This sale contains documents that have been exported to Xero. VOID or DELETE these in Xero: ${document_descriptions.join(", ")}`;
    toast.error(errorMessageText);
  } else {
    yield put(SaleAction.delete(getLivestockSaleId()));
  }
}

export function* updateSaleAndDeploymentSale(action) {
  const { livestockSaleId, salePayload, deploymentSales } = action;

  for (const deploymentSale of deploymentSales) {
    yield put(
      patchDeploymentSale(
        deploymentSale.payload,
        deploymentSale.deploymentSaleId,
        livestockSaleId,
        {
          updateExistingSaleLotExportSites:
            deploymentSale.payload?.updateExistingSaleLotExportSites,
        },
      ),
    );
  }

  yield put(SaleAction.update({ id: livestockSaleId, ...salePayload }));
}

export default function* rootSaga() {
  yield takeEvery(SALE.CREATE.SUCCESS, onSaleCreateSuccess);
  yield takeEvery(SALE.FETCH.SUCCESS, onSaleFetchSuccess);
  yield takeEvery(SALE.UPDATE.FAILURE, onSaleUpdateFailure);
  yield takeEvery(SALE.CHECK_AND_DELETE_CURRENT.REQUEST, deleteCurrentSale);
  yield takeEvery(
    SALE.UPDATE_WITH_DEPLOYMENT_SALES.REQUEST,
    updateSaleAndDeploymentSale,
  );
}
